/**
 * Sheet — выезжающая панель / drawer (Radix Dialog). Наш стиль:
 * arrow-const + ref-проп. Используется как карточка-drawer клиента.
 */

import type {ComponentPropsWithoutRef, ComponentRef, HTMLAttributes, Ref} from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import {cva, type VariantProps} from 'class-variance-authority';
import {X} from 'lucide-react';

import {cn} from '@lib/utils';

export const Sheet = SheetPrimitive.Root;
export const SheetTrigger = SheetPrimitive.Trigger;
export const SheetClose = SheetPrimitive.Close;
export const SheetPortal = SheetPrimitive.Portal;

type OverlayProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> & {
  ref?: Ref<ComponentRef<typeof SheetPrimitive.Overlay>>;
};
export const SheetOverlay = ({className, ref, ...props}: OverlayProps) => (
  <SheetPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
);

const sheetVariants = cva(
  'fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
        right:
          'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-md',
      },
    },
    defaultVariants: {side: 'right'},
  },
);

type ContentProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Content> &
  VariantProps<typeof sheetVariants> & {
    /** Показывать встроенный крестик закрытия (по умолчанию да). */
    showClose?: boolean;
    ref?: Ref<ComponentRef<typeof SheetPrimitive.Content>>;
  };
export const SheetContent = ({
  side = 'right',
  className,
  children,
  showClose = true,
  ref,
  ...props
}: ContentProps) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({side}), className)} {...props}>
      {children}
      {showClose && (
        <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
          <X className="size-4" />
          <span className="sr-only">Закрыть</span>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Content>
  </SheetPortal>
);

export const SheetHeader = ({className, ...props}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-left', className)} {...props} />
);

type TitleProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Title> & {
  ref?: Ref<ComponentRef<typeof SheetPrimitive.Title>>;
};
export const SheetTitle = ({className, ref, ...props}: TitleProps) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-foreground', className)}
    {...props}
  />
);

type DescriptionProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Description> & {
  ref?: Ref<ComponentRef<typeof SheetPrimitive.Description>>;
};
export const SheetDescription = ({className, ref, ...props}: DescriptionProps) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
);
